import { useState } from 'react';
import { photoService } from '../services/photoService';

export interface UploadProgress {
  fileName: string;
  status: 'pending' | 'getting-url' | 'uploading' | 'processing' | 'complete' | 'error';
  progress: number;
  error?: string;
  photoUrl?: string;
  thumbnailUrl?: string;
  optimizedUrl?: string;
}

export interface UsePhotoUploadResult {
  uploadPhoto: (file: File, displayOrder?: number) => Promise<{
    key: string;
    photoUrl: string;
    thumbnailUrl: string;
    optimizedUrl: string;
  }>;
  uploadPhotos: (files: File[]) => Promise<{
    key: string;
    photoUrl: string;
    thumbnailUrl: string;
    optimizedUrl: string;
  }[]>;
  uploading: boolean;
  uploadProgress: Record<string, UploadProgress>;
  resetProgress: () => void;
}

export const usePhotoUpload = (apartmentId: string): UsePhotoUploadResult => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({});

  const uploadPhoto = async (file: File, displayOrder?: number) => {
    const fileKey = `${Date.now()}_${file.name}`;

    try {
      // Update progress: pending
      setUploadProgress((prev) => ({
        ...prev,
        [fileKey]: { fileName: file.name, status: 'pending', progress: 0 },
      }));

      // Step 1: Get upload URL
      setUploadProgress((prev) => ({
        ...prev,
        [fileKey]: { fileName: file.name, status: 'getting-url', progress: 10 },
      }));

      const uploadData = await photoService.getUploadUrl(
        apartmentId,
        file.name,
        file.type
      );

      // Step 2: Upload to S3
      setUploadProgress((prev) => ({
        ...prev,
        [fileKey]: { fileName: file.name, status: 'uploading', progress: 20 },
      }));

      await photoService.uploadToS3(uploadData.uploadUrl, file, (progress) => {
        // Map 0-100 progress to 20-70 range
        const mappedProgress = 20 + Math.floor(progress * 0.5);
        setUploadProgress((prev) => ({
          ...prev,
          [fileKey]: {
            fileName: file.name,
            status: 'uploading',
            progress: mappedProgress,
          },
        }));
      });

      // Step 3: Confirm upload
      setUploadProgress((prev) => ({
        ...prev,
        [fileKey]: { fileName: file.name, status: 'processing', progress: 80 },
      }));

      await photoService.confirmPhoto(apartmentId, {
        key: uploadData.key,
        photoUrl: uploadData.photoUrl,
        thumbnailUrl: uploadData.thumbnailUrl,
        optimizedUrl: uploadData.optimizedUrl,
        displayOrder,
      });

      // Update progress: complete
      setUploadProgress((prev) => ({
        ...prev,
        [fileKey]: {
          fileName: file.name,
          status: 'complete',
          progress: 100,
          photoUrl: uploadData.photoUrl,
          thumbnailUrl: uploadData.thumbnailUrl,
          optimizedUrl: uploadData.optimizedUrl,
        },
      }));

      return {
        key: uploadData.key,
        photoUrl: uploadData.photoUrl,
        thumbnailUrl: uploadData.thumbnailUrl,
        optimizedUrl: uploadData.optimizedUrl,
      };
    } catch (error) {
      setUploadProgress((prev) => ({
        ...prev,
        [fileKey]: {
          fileName: file.name,
          status: 'error',
          progress: 0,
          error: error instanceof Error ? error.message : 'Upload failed',
        },
      }));
      throw error;
    }
  };

  const uploadPhotos = async (files: File[]) => {
    setUploading(true);
    try {
      const uploadPromises = files.map((file) => uploadPhoto(file));
      return await Promise.all(uploadPromises);
    } finally {
      setUploading(false);
    }
  };

  const resetProgress = () => {
    setUploadProgress({});
  };

  return {
    uploadPhoto,
    uploadPhotos,
    uploading,
    uploadProgress,
    resetProgress,
  };
};

